import Keycloak, { KeycloakConfig } from 'keycloak-js';

import { safeLs } from 'shared/storages';

import { _kcConfigs } from './config';

import { KeycloakTokens } from './types';

import { KEYCLOAK_TOKENS } from './constants';

class AuthenticationService {
  _kc: Keycloak;

  constructor() {
    this._kc = new Keycloak();
  }

  private async addTokensInLs(): Promise<KeycloakTokens> {
    return safeLs.add<KeycloakTokens>(KEYCLOAK_TOKENS, {
      token: this._kc.token,
      refreshToken: this._kc.refreshToken,
      idToken: this._kc.idToken,
    });
  }

  private getTokensFromLs() {
    const keycloakTokens = safeLs.get<KeycloakTokens, 'sync'>(KEYCLOAK_TOKENS, {
      parse: true,
      type: 'sync',
    });

    return keycloakTokens;
  }

  async initKeycloak(
    config: KeycloakConfig,
    allowAnonymous?: boolean,
  ): Promise<boolean | undefined> {
    const _kc: Keycloak = new Keycloak(config);
    this._kc = _kc;

    let authenticated: boolean | undefined = undefined;

    try {
      const keycloakTokens = this.getTokensFromLs();

      authenticated = await _kc.init({
        ..._kcConfigs,
        token: keycloakTokens?.token,
        refreshToken: keycloakTokens?.refreshToken,
        idToken: keycloakTokens?.idToken,
      });

      if (authenticated) {
        if (this._kc.token && this._kc.refreshToken) {
          await this.addTokensInLs();
        }
      }

      if (!authenticated && !allowAnonymous) {
        const fn = this.updateToken();

        fn();
      }
    } catch (error) {
      (error: unknown) => console.error(error);
    }

    return authenticated;
  }

  getToken() {
    const keycloakTokens = this.getTokensFromLs();

    return this._kc?.token ?? keycloakTokens?.token;
  }

  isLoggedIn() {
    return Boolean(this.getToken);
  }

  getLoginUrl() {
    return this._kc?.createLoginUrl();
  }

  getLogoutUrl() {
    return this._kc?.createLogoutUrl();
  }

  updateToken() {
    return async () => await this._kc.updateToken(5).catch(this._kc.login);
  }

  getRoles() {
    return this._kc.tokenParsed?.resource_access?.account;
  }

  login() {
    return this._kc.login();
  }

  logout() {
    safeLs.delete(KEYCLOAK_TOKENS);

    return this._kc.logout();
  }

  async refreshTokens() {
    return await this._kc
      .updateToken(5)
      .then(() => this.addTokensInLs())
      .catch(async () => {
        await this.logout();
      });
  }
}

export const KeyClockAuthService = new AuthenticationService();
